import React from 'react';
import { InAppBrowser, androidChromeUrl, iosExternalBrowserUrl } from '../utils/inAppBrowser';
import { firstTouchUrl } from '../analytics/acquisition';

// Sign-up inside an in-app browser (#937, #1106).
//
// Google refuses OAuth in these webviews, so AuthPage hides the Google and Apple buttons
// there. #937 put a "Sign up with Google or Apple" button at the top of the form instead,
// but on iPhone that is Meta's undocumented instagram://extbrowser link, which Instagram
// can ignore: a first-time user tapped it, nothing happened, and nothing said why (#1106).
// So the page now says plainly, above the form, that email is the way in here, and the note
// links out to the browser where it can (#1111), with the menu step as the fallback.
//
// The iPhone copy says "your browser", never "Safari": these menus open the default
// browser, which can be Chrome (owner, #937). The menu item is named only for Meta's apps,
// as the owner gave it (#1111). Links are real anchors: these webviews honour only a
// declarative navigation (see inAppBrowser.ts), and nothing fires on load. They carry the
// first touch, so the account is still credited to the ad after the switch.

/**
 * The link out, or null when there is none (iPhone, any app but Instagram). Runs during
 * render and firstTouchUrl uses new URL(), so any throw becomes null and the text
 * fallback shows: the sign-up page must never go blank over a convenience.
 */
export function signUpEscapeHref(inApp: InAppBrowser, origin: string): string | null {
  try {
    const target = firstTouchUrl(`${origin}/register`, inApp.app);
    return inApp.isAndroid ? androidChromeUrl(target) : iosExternalBrowserUrl(inApp.app, target);
  } catch {
    return null;
  }
}

// The menu step. Instagram and Facebook share Meta's in-app browser, whose menu item the
// owner names: "Open in system browser" (#1111). Other apps' menus may label it otherwise,
// so they get the general words.
const META_APPS = ['Instagram', 'Facebook'];
export function menuStep(inApp: InAppBrowser): string {
  return META_APPS.includes(inApp.app)
    ? 'tap the ⋯ menu and choose "Open in system browser"'
    : 'tap the ⋯ menu and open this page in your browser';
}

/**
 * Above the sign-up form: what works here (#1106), and where a link out exists (Instagram
 * on iPhone, any app on Android) the note itself links to the browser (#1111). Without
 * one, it gives the menu step.
 */
export const InAppSignUpNote: React.FC<{ inApp: InAppBrowser }> = ({ inApp }) => {
  const href = signUpEscapeHref(inApp, window.location.origin);
  const browser = inApp.isAndroid ? 'Chrome' : 'your browser';
  return (
    <p className="inapp-signup-note">
      <strong>{`Inside ${inApp.app}, sign up with your email.`}</strong>
      {` Google and Apple sign-in don't work in ${inApp.app}'s browser`}
      {href
        ? <>{', so '}<a href={href}>{`open this page in ${browser}`}</a>{' to use them.'}</>
        : `. To use them, ${menuStep(inApp)}.`}
    </p>
  );
};

/**
 * Under the sign-up form: the fallback, only where the note's link can silently fail, which
 * is iPhone (Instagram's link out is undocumented and Instagram can ignore it). Android's
 * Chrome link is dependable, and without a link the note already gives the menu step.
 */
const InAppSignUpEscape: React.FC<{ inApp: InAppBrowser }> = ({ inApp }) => {
  if (inApp.isAndroid || !signUpEscapeHref(inApp, window.location.origin)) return null;
  return (
    <div className="inapp-signup-escape">
      <p className="inapp-signup-escape-text">{`If the link doesn't open your browser, ${menuStep(inApp)}.`}</p>
    </div>
  );
};

export default InAppSignUpEscape;
